import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { Location } from 'history';
import { escapeRegExp, fromPairs } from 'lodash';

import { PageConfigMap } from '@/entities/page-config-map';
import { PageUrlKey } from '@/entities/page-url-key';
import type { PageConfig } from '@/types/page-config';

interface PageContextValue {
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  urlKey: PageUrlKey | undefined;
  pageConfig: PageConfig | undefined;
  location: Location;
}

const PageContext = createContext<PageContextValue | undefined>(undefined);

export function usePageContext() {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePageContext must be used within a PageContextProvider');
  }
  return context;
}

interface PageContextProviderProps {
  children: ReactNode;
}

export function PageContextProvider({ children }: PageContextProviderProps) {
  const location = useLocation();
  const [filters, setFilters] = useState<Record<string, string>>({});

  const { urlKey, pageConfig } = useMemo(() => {
    const pageConfigEntry = Object.entries(PageConfigMap).find(([key]) =>
      location.pathname.match(new RegExp(`/${escapeRegExp(key)}`)),
    );

    return {
      urlKey: pageConfigEntry?.[0] as PageUrlKey | undefined,
      pageConfig: pageConfigEntry?.[1],
    };
  }, [location]);

  useEffect(() => {
    setFilters(fromPairs(Array.from(new URLSearchParams(location.search))));
  }, [location.search]);

  const value: PageContextValue = {
    filters,
    setFilters,
    urlKey,
    pageConfig,
    location,
  };

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}
